// Types
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { getMenuProps } from 'components/ContextMenu';
import LoadingWrapper from 'components/LoadingWrapper';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
// Utils
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
// Slices
import {
  checkFirstMembershipExist,
  createNewMembership,
  deleteMembership,
  duplicateMembership,
  membershipMapSelector,
  membershipsLoading,
  membershipsSelector,
  selectedIdsSelector,
  setFirstMembership,
  setSecondMembership,
} from 'slices/memberships.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { matchString } from 'utils/stringUtils';
import { EReservationKind, type TMemberType } from '@timeedit/types';
// Components
import {
  Button,
  EditableTable,
  Filter,
  TEEFilterType,
  type TEIFilter,
  type TEIFilterField,
  type TEIOption,
} from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';
// Styles
import './MembershipList.scss';

export interface IRowData {
  id: number;
  name: string;
  type: string;
}

interface Props {
  confirmDiscardChanges: (isFirstDrawer: boolean, actionFn: () => void) => void;
}

const MembershipList = ({ confirmDiscardChanges }: Props) => {
  const dispatch = useAppDispatch();

  const memberships: TMemberType[] = useAppSelector(membershipsSelector);
  const membershipMap: Record<number, TMemberType> = useAppSelector(
    membershipMapSelector
  );
  const [highlightIds, setHighlightIds] = useState<number[]>([]);
  const selectedIds: number[] = useAppSelector(selectedIdsSelector);

  useEffect(() => {
    setTimeout(() => {
      setHighlightIds(selectedIds);
    }, 100);
  }, [selectedIds.join('-')]);

  const loading = useAppSelector(membershipsLoading);

  const [filterSettings, setFilterSettings] = useState<TEIFilter>({
    filters: {},
    freeTextSearch: '',
  });

  const [confirmDelete, contextHolder] = useDeleteConfirmation();

  const filterData: IRowData[] = useMemo(() => {
    const dataSource = memberships.map((membership) => ({
      id: membership.id,
      name: membership.name || getText('new_membership'),
      type: getText(membership.reservationKind),
    }));

    return dataSource.filter((pr) => {
      let matchedType = true;
      let matchedText = true;
      const {
        filters: { type },
        freeTextSearch,
      } = filterSettings;
      if (type) {
        matchedType = pr.type === type;
      }
      if (freeTextSearch) {
        matchedText = matchString(pr.name, freeTextSearch);
      }
      return matchedType && matchedText;
    });
  }, [memberships, filterSettings]);

  const onDuplicate = useCallback(
    (record: IRowData) => {
      confirmDiscardChanges(true, () =>
        dispatch(duplicateMembership(membershipMap[record.id]))
      );
    },
    [membershipMap]
  );

  const onCompare = useCallback(
    (record: IRowData) => {
      const firstMembershipExist = dispatch(checkFirstMembershipExist());
      if (!firstMembershipExist) {
        return dispatch(setFirstMembership(membershipMap[record.id]));
      }
      confirmDiscardChanges(false, () =>
        dispatch(setSecondMembership(membershipMap[record.id]))
      );
    },
    [membershipMap]
  );

  const onRowClick = useCallback(
    (record: IRowData) => {
      confirmDiscardChanges(true, () =>
        dispatch(setFirstMembership(membershipMap[record.id]))
      );
    },
    [membershipMap]
  );

  const onRow = useRowProps(onRowClick, highlightIds);

  const onDelete = useCallback(
    (record: IRowData) =>
      confirmDelete(record.name, () => dispatch(deleteMembership(record.id))),
    [confirmDelete, dispatch]
  );

  const checkDrawerOpen = useCallback(
    () => Boolean(dispatch(checkFirstMembershipExist())),
    [dispatch]
  );

  const menuProps = useCallback(
    (record: IRowData) =>
      getMenuProps<IRowData>({
        record,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen,
        title: record.name,
      }),
    [onCompare, onDuplicate, onDelete, checkDrawerOpen]
  );

  const footer = useCallback(
    () => (
      <Space>
        <Button
          size="small"
          type="primary"
          onClick={() => dispatch(createNewMembership())}
        >
          Create new
        </Button>
      </Space>
    ),
    []
  );

  const [contentRef, defaultTableProps] = useDefaultTableProps();

  const title = useTableTitle<IRowData>({
    total: memberships.length,
    filteredAmount: filterData.length,
  });

  return (
    <div className="membership-list" ref={contentRef}>
      <Filter
        defaultFields={defaultFields}
        options={options}
        settings={filterSettings}
        onChange={setFilterSettings}
      />
      <LoadingWrapper isLoading={loading}>
        <EditableTable
          dataSource={filterData}
          columns={columns}
          getMenuProps={menuProps}
          onRow={onRow}
          footer={footer}
          title={title}
          {...defaultTableProps}
        />
      </LoadingWrapper>
      {contextHolder}
    </div>
  );
};

export default memo(MembershipList);

const columns = [
  {
    key: 'name',
    dataIndex: 'name',
    title: 'Name',
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.name.localeCompare(item2.name),
  },
  {
    key: 'type',
    dataIndex: 'type',
    title: 'Type',
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.type.localeCompare(item2.type),
  },
];

const defaultFields: TEIFilterField[] = [
  {
    label: 'Type',
    key: 'type',
    type: TEEFilterType.text,
  },
];
const options: Record<string, TEIOption[]> = {
  type: [EReservationKind.ABSTRACT, EReservationKind.PHYSICAL].map((type) => ({
    value: getText(type),
    label: getText(type),
  })),
};
